// pages/Agents/Workflows/WorkflowRunDashboard.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper, Box, Typography, Button, Divider, Alert, CircularProgress,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, IconButton, Pagination, Tabs, Tab, Card, CardContent, Grid,
  LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import SurveyIcon from "@mui/icons-material/Poll";
import EmailIcon from "@mui/icons-material/Email";
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import ContactsIcon from "@mui/icons-material/Contacts";
import SmsIcon from "@mui/icons-material/Sms";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ChatIcon from "@mui/icons-material/Chat";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import StarIcon from "@mui/icons-material/Star";
import WarningIcon from "@mui/icons-material/Warning";
import Rating from "@mui/material/Rating";

const statusColors = {
  PENDING: "default",
  RUNNING: "info",
  COMPLETED: "success",
  FAILED: "error",
};

// --- Sentiment Analysis Result (Twitter-style) ---

const SentimentCard = ({ label, count, total, icon, color }) => {
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
  return (
    <Paper sx={{ p: 2, textAlign: "center" }}>
      <Box display="flex" justifyContent="center" mb={1}>{icon}</Box>
      <Typography variant="h4">{count}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <LinearProgress
        variant="determinate"
        value={parseFloat(pct)}
        color={color}
        sx={{ mt: 1, height: 8, borderRadius: 4 }}
      />
      <Typography variant="caption">{pct}%</Typography>
    </Paper>
  );
};

const IntentBadge = ({ intent }) => {
  const colors = {
    support_request: "info", feature_request: "secondary", appreciation: "success",
    complaint: "error", question: "primary", feedback: "default",
  };
  return (
    <Chip
      label={intent?.replace(/_/g, " ")}
      size="small"
      color={colors[intent] || "default"}
    />
  );
};

const SentimentIcon = ({ sentiment }) => {
  if (sentiment === "positive") return <SentimentVerySatisfiedIcon color="success" fontSize="small" />;
  if (sentiment === "negative") return <SentimentVeryDissatisfiedIcon color="error" fontSize="small" />;
  return <SentimentNeutralIcon color="action" fontSize="small" />;
};

function SentimentAnalysisResult({ analysis }) {
  const sd = analysis.sentiment_distribution || {};
  const total = (sd.positive || 0) + (sd.negative || 0) + (sd.neutral || 0);
  const id = analysis.intent_distribution || {};
  const themes = analysis.key_themes || [];
  const urgent = analysis.urgent_issues || [];
  const topResp = analysis.top_responses || [];
  const wf = analysis.word_frequencies || {};
  const topWords = Object.entries(wf).sort((a, b) => b[1] - a[1]).slice(0, 15);

  return (
    <Box sx={{ mt: 1 }}>
      {/* Sentiment Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <SentimentCard
            label="Positive" count={sd.positive || 0} total={total}
            icon={<SentimentVerySatisfiedIcon sx={{ fontSize: 36, color: "success.main" }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SentimentCard
            label="Neutral" count={sd.neutral || 0} total={total}
            icon={<SentimentNeutralIcon sx={{ fontSize: 36, color: "grey.500" }} />}
            color="inherit"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <SentimentCard
            label="Negative" count={sd.negative || 0} total={total}
            icon={<SentimentVeryDissatisfiedIcon sx={{ fontSize: 36, color: "error.main" }} />}
            color="error"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="h4">{total}</Typography>
            <Typography variant="body2" color="text.secondary">Total Analyzed</Typography>
            {analysis.star_rating && (
              <Rating value={analysis.star_rating} precision={0.5} readOnly size="small" sx={{ mt: 1 }} />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Intent Distribution */}
      {Object.keys(id).length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Intent Distribution</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {Object.entries(id).filter(([, c]) => c > 0).map(([intent, count]) => (
              <Chip
                key={intent}
                label={`${intent.replace(/_/g, " ")}: ${count}`}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Key Themes */}
      {themes.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Key Themes</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {themes.map((t, i) => <Chip key={i} label={t} size="small" color="primary" variant="outlined" />)}
          </Box>
        </Paper>
      )}

      {/* Urgent Issues */}
      {urgent.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: "4px solid", borderColor: "error.main" }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" fontSize="small" /> Urgent Issues
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {urgent.map((u, i) => <Chip key={i} label={u} size="small" color="error" />)}
          </Box>
        </Paper>
      )}

      {/* Top Responses */}
      {topResp.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Top Responses</Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Response</TableCell>
                  <TableCell align="center">Sentiment</TableCell>
                  <TableCell align="center">Intent</TableCell>
                  <TableCell align="center">Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topResp.slice(0, 15).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {r.text?.substring(0, 150)}{r.text?.length > 150 ? "..." : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <SentimentIcon sentiment={r.sentiment} />
                    </TableCell>
                    <TableCell align="center">
                      {r.intent && <IntentBadge intent={r.intent} />}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">{r.sentiment_score}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Word Cloud */}
      {topWords.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Top Keywords</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {topWords.map(([word, count]) => (
              <Chip key={word} label={`${word} (${count})`} size="small" variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}

      {/* Summary */}
      {analysis.summary && (
        <Paper
          sx={{
            p: 2.5,
            background: "linear-gradient(135deg, #f0f9ff 0%, #eff6ff 100%)",
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontWeight: 700,
              color: "primary.dark",
            }}
          >
            Summary
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.8 }}>
            {analysis.summary}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

// --- Run History Tab ---

function RunRow({ run }) {
  const [open, setOpen] = useState(false);
  const [expandedSentiment, setExpandedSentiment] = useState(null);

  const duration =
    run.started_at && run.completed_at
      ? `${((new Date(run.completed_at) - new Date(run.started_at)) / 1000).toFixed(1)}s`
      : "-";

  return (
    <>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <TableCell>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>#{run.id}</Typography>
        </TableCell>
        <TableCell>
          <Chip label={run.status} color={statusColors[run.status]} size="small" />
        </TableCell>
        <TableCell>
          <Chip label={run.trigger_type} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            label={`${run.step_runs?.length || 0} steps`}
            size="small"
            variant="outlined"
            color="primary"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">{duration}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {run.created_at ? new Date(run.created_at).toLocaleString() : "-"}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              {run.error_message && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {run.error_message}
                </Alert>
              )}

              {run.step_runs && run.step_runs.length > 0 ? (
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Input</TableCell>
                      <TableCell>Output</TableCell>
                      <TableCell>Error</TableCell>
                      <TableCell>Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...run.step_runs].sort((a, b) => a.id - b.id).map((sr, idx) => {
                      const srDuration =
                        sr.started_at && sr.completed_at
                          ? `${((new Date(sr.completed_at) - new Date(sr.started_at)) / 1000).toFixed(1)}s`
                          : "-";
                      const hasSentiment = (sr.action_type === "survey.trigger_sentiment" && sr.output_data?.analysis) || (["chat.trigger_history_sentiment", "chat.trigger_feedback_sentiment"].includes(sr.action_type) && sr.output_data?.analysis);
                      return (
                        <React.Fragment key={sr.id}>
                          <TableRow>
                            <TableCell>
                              <Typography variant="caption" fontWeight={600}>
                                {idx + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" fontWeight={600}>
                                {sr.action_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={sr.status}
                                color={statusColors[sr.status]}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: "hidden" }}>
                              <Box
                                component="pre"
                                sx={{
                                  fontSize: 11,
                                  bgcolor: "grey.100",
                                  p: 0.5,
                                  borderRadius: 0.5,
                                  overflow: "auto",
                                  maxHeight: 80,
                                  maxWidth: "100%",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  m: 0,
                                }}
                              >
                                {sr.input_data ? JSON.stringify(sr.input_data, null, 2) : "-"}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: "hidden" }}>
                              {hasSentiment ? (
                                <Chip
                                  label={expandedSentiment === sr.id ? "Hide Analysis" : "View Sentiment Analysis"}
                                  size="small"
                                  color="primary"
                                  clickable
                                  onClick={() => setExpandedSentiment(expandedSentiment === sr.id ? null : sr.id)}
                                />
                              ) : (
                                <Box
                                  component="pre"
                                  sx={{
                                    fontSize: 11,
                                    bgcolor: "grey.100",
                                    p: 0.5,
                                    borderRadius: 0.5,
                                    overflow: "auto",
                                    maxHeight: 80,
                                    maxWidth: "100%",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    m: 0,
                                  }}
                                >
                                  {sr.output_data ? JSON.stringify(sr.output_data, null, 2) : "-"}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {sr.error_message && (
                                <Typography variant="caption" color="error">
                                  {sr.error_message}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{srDuration}</TableCell>
                          </TableRow>
                          {hasSentiment && expandedSentiment === sr.id && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0 }}>
                                <Box sx={{ p: 2, bgcolor: "grey.50" }}>
                                  <SentimentAnalysisResult analysis={sr.output_data.analysis} />
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No step execution data available.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// --- Console Tab ---

function ConsoleEntry({ entry }) {
  const [open, setOpen] = useState(false);

  const statusColor = {
    COMPLETED: "success.main",
    FAILED: "error.main",
    RUNNING: "info.main",
    PENDING: "text.secondary",
  };

  const dotColor = {
    COMPLETED: "#4caf50",
    FAILED: "#f44336",
    RUNNING: "#2196f3",
    PENDING: "#9e9e9e",
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        py: 1.5,
        px: 2,
        borderLeft: "3px solid",
        borderColor: dotColor[entry.status] || "#9e9e9e",
        mb: 1,
        bgcolor: "background.default",
        borderRadius: "0 8px 8px 0",
        cursor: "pointer",
        "&:hover": { bgcolor: "action.hover" },
      }}
      onClick={() => setOpen(!open)}
    >
      <Box sx={{ minWidth: 140 }}>
        <Typography variant="caption" color="text.secondary">
          {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "-"}
        </Typography>
        {entry.duration_ms != null && (
          <Typography variant="caption" display="block" color="text.secondary">
            {entry.duration_ms}ms
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <Typography variant="subtitle2" fontWeight={700} color={statusColor[entry.status]} sx={{ wordBreak: "break-word" }}>
            {entry.insight}
          </Typography>
          <Chip label={entry.status} color={statusColors[entry.status]} size="small" />
          {entry.step_number && (
            <Chip label={`Step ${entry.step_number}`} size="small" color="primary" variant="outlined" />
          )}
          <Chip label={`Run #${entry.run_id}`} size="small" variant="outlined" />
        </Box>
        <Chip label={entry.action_type} size="small" variant="outlined" sx={{ mt: 0.5, fontSize: 11 }} />

        {entry.error_message && (
          <Alert severity="error" sx={{ mt: 1, py: 0 }}>
            {entry.error_message}
          </Alert>
        )}

        <Collapse in={open} timeout="auto">
          <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap", overflow: "hidden" }}>
            {entry.input_data && (
              <Box sx={{ flex: 1, minWidth: 200, maxWidth: "100%", overflow: "hidden" }}>
                <Typography variant="caption" fontWeight={600}>Input</Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: 11,
                    bgcolor: "grey.900",
                    color: "grey.100",
                    p: 1,
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 150,
                    maxWidth: "100%",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    m: 0,
                  }}
                >
                  {JSON.stringify(entry.input_data, null, 2)}
                </Box>
              </Box>
            )}
            {entry.output_data && (
              <Box sx={{ flex: 1, minWidth: 200, maxWidth: "100%", overflow: "hidden" }}>
                <Typography variant="caption" fontWeight={600}>Output</Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: 11,
                    bgcolor: "grey.900",
                    color: "grey.100",
                    p: 1,
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 150,
                    maxWidth: "100%",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    m: 0,
                  }}
                >
                  {JSON.stringify(entry.output_data, null, 2)}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

function ConsoleTab({ workflowUid }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLive, setIsLive] = useState(false);

  const fetchConsole = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/workflows/${workflowUid}/console`, {
        params: { page, per_page: 50 },
      });
      const data = res.data.data || [];
      setEntries(data);
      setTotalPages(res.data.pages || 1);
      // Check if any entry is RUNNING to enable live polling
      const hasRunning = data.some((e) => e.status === "RUNNING");
      setIsLive(hasRunning);
    } catch {
      if (!silent) setError("Failed to load console logs.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [workflowUid, page]);

  useEffect(() => {
    fetchConsole();
  }, [fetchConsole]);

  // Real-time polling when a run is active
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => fetchConsole(true), 5000);
    return () => clearInterval(interval);
  }, [isLive, fetchConsole]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Activity Console
          </Typography>
          {isLive && (
            <Chip
              icon={<FiberManualRecordIcon sx={{ fontSize: 10, animation: "pulse 1.5s infinite", "@keyframes pulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />}
              label="Live"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => fetchConsole()} variant="outlined" size="small">
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {entries.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography variant="body2" color="text.secondary">
            No activity recorded yet. Run the workflow to see activity here.
          </Typography>
        </Box>
      ) : (
        <>
          {(() => {
            // Group entries by run_id
            const grouped = {};
            entries.forEach((entry) => {
              const key = entry.run_id;
              if (!grouped[key]) grouped[key] = [];
              grouped[key].push(entry);
            });
            // Sort run groups by run_id descending (latest run first)
            const runIds = Object.keys(grouped).sort((a, b) => Number(b) - Number(a));
            return runIds.map((runId) => {
              const group = grouped[runId];
              // Sort steps descending: highest step_number on top, step 1 at bottom
              const sorted = [...group].sort((a, b) => (b.step_number || 0) - (a.step_number || 0));
              const runStatus = sorted[0]?.status;
              return (
                <Box key={runId} sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Run #{runId}
                    </Typography>
                    <Chip label={runStatus} color={statusColors[runStatus]} size="small" />
                    <Typography variant="caption" color="text.secondary">
                      {sorted.length} step{sorted.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ borderLeft: "2px solid", borderColor: "divider", ml: 1, pl: 0 }}>
                    {sorted.map((entry) => (
                      <ConsoleEntry key={entry.id} entry={entry} />
                    ))}
                  </Box>
                </Box>
              );
            });
          })()}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// --- Activity Summary Tab ---

const resourceIcons = {
  surveys: <SurveyIcon />,
  events: <EventIcon />,
  volunteers: <PeopleIcon />,
  contacts: <ContactsIcon />,
  leads: <ContactsIcon />,
  tickets: <ConfirmationNumberIcon />,
  opportunities: <AssessmentIcon />,
  communications: <EmailIcon />,
  chat: <ChatIcon />,
  other: <AssessmentIcon />,
};

const resourceColors = {
  surveys: "#9c27b0",
  events: "#ff9800",
  volunteers: "#4caf50",
  contacts: "#00bcd4",
  leads: "#2196f3",
  tickets: "#f44336",
  opportunities: "#673ab7",
  communications: "#e91e63",
  chat: "#00897b",
  other: "#607d8b",
};

const resourceLabels = {
  surveys: "Surveys",
  events: "Events",
  volunteers: "Volunteers",
  contacts: "Contacts",
  leads: "Leads",
  tickets: "Tickets",
  opportunities: "Opportunities",
  communications: "Communications",
  chat: "Chat Analysis",
  other: "Other",
};

const verdictColors = {
  success: "success.main",
  warning: "warning.main",
  error: "error.main",
  info: "info.main",
};

const verdictBgColors = {
  success: "#e8f5e9",
  warning: "#fff3e0",
  error: "#ffebee",
  info: "#e3f2fd",
};

function ActivityCard({ activity }) {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box
      sx={{
        p: 2,
        mb: 1,
        borderRadius: 1,
        bgcolor: activity.verdict_type ? verdictBgColors[activity.verdict_type] : "grey.50",
        borderLeft: "3px solid",
        borderLeftColor: activity.status === "success" ? "success.main" : "error.main",
        cursor: activity.details?.length > 0 ? "pointer" : "default",
      }}
      onClick={() => activity.details?.length > 0 && setExpanded(!expanded)}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" fontWeight={600}>
            {activity.summary}
          </Typography>
          {activity.verdict && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: verdictColors[activity.verdict_type] || "text.primary" }}
              fontWeight={500}
            >
              {activity.verdict}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatDate(activity.timestamp)}
          </Typography>
          {activity.details?.length > 0 && (
            <IconButton size="small">
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={expanded}>
        {activity.details && activity.details.length > 0 && (
          <Box sx={{ mt: 1.5, pl: 1, borderLeft: "2px solid", borderColor: "divider" }}>
            {activity.details.map((detail, idx) => (
              <Typography key={idx} variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                {detail}
              </Typography>
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

function RunGroup({ runData }) {
  const [expanded, setExpanded] = useState(false);

  const modeLabel = runData.mode === "AI_AGENT" ? "AI Agent" : "Workflow Pipeline";
  const modeColor = runData.mode === "AI_AGENT" ? "secondary" : "primary";

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box
      sx={{
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Run Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          bgcolor: runData.mode === "AI_AGENT" ? "#f3e5f5" : "#e3f2fd",
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Run #{runData.run_id}
          </Typography>
          <Chip
            label={modeLabel}
            size="small"
            color={modeColor}
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={runData.status}
            size="small"
            color={statusColors[runData.status]}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatDate(runData.started_at)}
          </Typography>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Summary Section */}
      <Box sx={{ p: 2, borderBottom: expanded ? "1px solid" : "none", borderColor: "divider" }}>
        {/* Summary Text */}
        {runData.summary && (
          <Typography variant="body1" sx={{ mb: 1 }}>
            {runData.summary}
          </Typography>
        )}

        {/* Final Verdict */}
        {runData.final_verdict && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 1,
              bgcolor: verdictBgColors[runData.verdict_type] || "#f5f5f5",
              borderLeft: "4px solid",
              borderLeftColor: verdictColors[runData.verdict_type] || "grey.500",
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              {runData.final_verdict}
            </Typography>
          </Box>
        )}

        {/* Show details toggle */}
        {runData.activities && runData.activities.length > 0 && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{ mt: 1 }}
          >
            {expanded ? "Hide details" : "Show details"} ({runData.activities.length} actions)
          </Button>
        )}
      </Box>

      {/* Detailed Activities */}
      <Collapse in={expanded}>
        <Box sx={{ p: 1.5, bgcolor: "grey.50" }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Detailed Actions:
          </Typography>
          {runData.activities && runData.activities.length > 0 ? (
            runData.activities.map((activity, idx) => (
              <ActivityCard key={idx} activity={activity} />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No activities in this run.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function ResourceSection({ resource, data }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderLeft: `4px solid ${resourceColors[resource] || resourceColors.other}`,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            mb: expanded ? 2 : 0,
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ color: resourceColors[resource] || resourceColors.other, display: "flex" }}>
              {resourceIcons[resource] || resourceIcons.other}
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {resourceLabels[resource] || resource}
            </Typography>
            <Chip
              label={`${data.total_actions} actions`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${data.success_rate}% success`}
              size="small"
              color={data.success_rate >= 80 ? "success" : data.success_rate >= 50 ? "warning" : "error"}
            />
          </Box>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        {/* Success Rate Bar */}
        <Collapse in={expanded}>
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={data.success_rate || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: "grey.200",
                "& .MuiLinearProgress-bar": {
                  bgcolor: data.success_rate >= 80 ? "success.main" : data.success_rate >= 50 ? "warning.main" : "error.main",
                },
              }}
            />
          </Box>

          {/* Runs grouped */}
          {data.runs && data.runs.length > 0 ? (
            <Box>
              {data.runs.map((runData) => (
                <RunGroup key={runData.run_id} runData={runData} />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No activities recorded yet.
            </Typography>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

// --- AI Analysis for a single run's resource ---

function AnalysisResourceCard({ resource, data }) {
  const [expanded, setExpanded] = useState(true);
  const sd = data.sentiment_distribution || {};
  const total = (sd.positive || 0) + (sd.negative || 0) + (sd.neutral || 0);
  const id = data.intent_distribution || {};
  const themes = data.key_themes || [];
  const urgent = data.urgent_issues || [];
  const insights = data.top_insights || [];

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderLeft: `4px solid ${resourceColors[resource] || resourceColors.other}`,
      }}
    >
      <CardContent>
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", mb: expanded ? 2 : 0 }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ color: resourceColors[resource] || resourceColors.other, display: "flex" }}>
              {resourceIcons[resource] || resourceIcons.other}
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {resourceLabels[resource] || resource}
            </Typography>
            {data.star_rating && (
              <Rating value={data.star_rating} precision={0.5} readOnly size="small" />
            )}
            <Chip
              label={`${data.success_rate || 0}% success`}
              size="small"
              color={(data.success_rate || 0) >= 80 ? "success" : (data.success_rate || 0) >= 50 ? "warning" : "error"}
            />
          </Box>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {/* Sentiment Cards */}
          {total > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <SentimentCard
                  label="Positive" count={sd.positive || 0} total={total}
                  icon={<SentimentVerySatisfiedIcon sx={{ fontSize: 36, color: "success.main" }} />}
                  color="success"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <SentimentCard
                  label="Neutral" count={sd.neutral || 0} total={total}
                  icon={<SentimentNeutralIcon sx={{ fontSize: 36, color: "grey.500" }} />}
                  color="inherit"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <SentimentCard
                  label="Negative" count={sd.negative || 0} total={total}
                  icon={<SentimentVeryDissatisfiedIcon sx={{ fontSize: 36, color: "error.main" }} />}
                  color="error"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="h4">{data.actions_taken || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Actions Taken</Typography>
                  {data.star_rating && (
                    <Rating value={data.star_rating} precision={0.5} readOnly size="small" sx={{ mt: 1 }} />
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Intent Distribution */}
          {Object.keys(id).length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Intent Distribution</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {Object.entries(id).filter(([, c]) => c > 0).map(([intent, count]) => (
                  <Chip key={intent} label={`${intent.replace(/_/g, " ")}: ${count}`} variant="outlined" size="small" />
                ))}
              </Box>
            </Paper>
          )}

          {/* Key Themes */}
          {themes.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Key Themes</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {themes.map((t, i) => <Chip key={i} label={t} size="small" color="primary" variant="outlined" />)}
              </Box>
            </Paper>
          )}

          {/* Urgent Issues */}
          {urgent.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, borderLeft: "4px solid", borderColor: "error.main" }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WarningIcon color="error" fontSize="small" /> Urgent Issues
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {urgent.map((u, i) => <Chip key={i} label={u} size="small" color="error" />)}
              </Box>
            </Paper>
          )}

          {/* Top Insights */}
          {insights.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Top Insights</Typography>
              {insights.map((ins, i) => (
                <Typography key={i} variant="body2" sx={{ py: 0.25, pl: 1, borderLeft: "2px solid", borderColor: "primary.main", mb: 0.5 }}>
                  {ins}
                </Typography>
              ))}
            </Paper>
          )}

          {/* Summary */}
          {data.summary && (
            <Paper
              sx={{
                p: 2.5,
                mb: 2,
                background: "linear-gradient(135deg, #f0f9ff 0%, #eff6ff 100%)",
                border: "1px solid",
                borderColor: "primary.light",
                borderRadius: 3,
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontWeight: 700,
                  color: "primary.dark",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                Summary
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.8 }}>
                {data.summary}
              </Typography>
            </Paper>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

// --- Analyzed Run Card ---

function AnalyzedRunCard({ runData, selectedResource }) {
  const analysis = runData.analysis_data;
  if (!analysis) return null;

  const verdictColorMap = {
    SUCCESS: "success", WARNING: "warning", FAILURE: "error",
  };
  const verdictColor = verdictColorMap[analysis.overall_verdict] || "info";

  // Filter resources by selected resource if provided, always exclude "other"
  const resources = analysis.resources || {};
  const filteredResources = selectedResource
    ? Object.entries(resources).filter(([r]) => r === selectedResource)
    : Object.entries(resources).filter(([r]) => r !== "other").sort(([, a], [, b]) => (b.actions_taken || 0) - (a.actions_taken || 0));

  const duration = runData.started_at && runData.completed_at
    ? `${((new Date(runData.completed_at) - new Date(runData.started_at)) / 1000).toFixed(1)}s`
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: verdictColor === "success" ? "success.light" : verdictColor === "error" ? "error.light" : "divider",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2.5,
          background: verdictColor === "success"
            ? "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)"
            : verdictColor === "error"
            ? "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)"
            : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: verdictColor === "success" ? "success.main" : verdictColor === "error" ? "error.main" : "primary.main",
            color: "#fff", fontWeight: 800, fontSize: 14,
          }}>
            #{runData.run_id}
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>Run #{runData.run_id}</Typography>
              <Chip label={runData.status} size="small" color={statusColors[runData.status]} />
              <Chip label={runData.trigger_type} size="small" variant="outlined" />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {runData.started_at ? new Date(runData.started_at).toLocaleString() : ""}
              {duration && ` — ${duration}`}
            </Typography>
          </Box>
        </Box>
        {analysis.overall_verdict && (
          <Chip
            label={analysis.overall_verdict}
            size="small"
            color={verdictColor}
            sx={{ fontWeight: 700 }}
          />
        )}
      </Box>

      {/* Summary & Verdict */}
      <Box sx={{ p: 2.5 }}>
        {analysis.summary && (
          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>{analysis.summary}</Typography>
        )}
        {analysis.verdict_message && (
          <Alert severity={verdictColor} sx={{ mb: 2, borderRadius: 2 }} variant="outlined">
            {analysis.verdict_message}
          </Alert>
        )}

        {/* Resource-level analysis — always visible */}
        {filteredResources.length > 0 && (
          <Box>
            {filteredResources.map(([resource, resData]) => (
              <AnalysisResourceCard key={resource} resource={resource} data={resData} />
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
}

// --- Activity Summary / Analysis Tab ---

function ActivitySummaryTab({ workflowUid }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("runs"); // "runs" or "resources"
  const [selectedResource, setSelectedResource] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/workflows/${workflowUid}/activity-summary`);
      const data = res.data.data || null;
      setSummary(data);
      // Auto-select "surveys" if available, otherwise first resource
      if (data?.resources && !selectedResource) {
        const keys = Object.keys(data.resources).filter((r) => r !== "other");
        if (keys.includes("surveys")) {
          setSelectedResource("surveys");
        } else if (keys.length > 0) {
          setSelectedResource(keys.sort((a, b) =>
            (data.resources[b]?.total_actions || 0) - (data.resources[a]?.total_actions || 0)
          )[0]);
        }
      }
    } catch {
      setError("Failed to load analysis.");
    } finally {
      setLoading(false);
    }
  }, [workflowUid]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const hasAnalyzedRuns = summary?.analyzed_runs && summary.analyzed_runs.length > 0;
  const hasResources = summary?.resources && Object.keys(summary.resources).filter((r) => r !== "other").length > 0;
  const availableResources = hasResources
    ? Object.keys(summary.resources)
        .filter((r) => r !== "other")
        .sort((a, b) =>
          (summary.resources[b]?.total_actions || 0) - (summary.resources[a]?.total_actions || 0)
        )
    : [];

  if (!hasAnalyzedRuns && !hasResources) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <AssessmentIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No analysis yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Run the workflow to see AI-powered analysis here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* View mode + resource selector bar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>

          {/* Resource filter — visible in both modes */}
          {availableResources.length > 0 && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              {availableResources.map((r) => (
                <Chip
                  key={r}
                  icon={resourceIcons[r] || resourceIcons.other}
                  label={resourceLabels[r] || r}
                  size="small"
                  variant={selectedResource === r ? "filled" : "outlined"}
                  color={selectedResource === r ? "primary" : "default"}
                  onClick={() => setSelectedResource(r)}
                  clickable
                  sx={{
                    borderColor: selectedResource === r ? undefined : resourceColors[r] || resourceColors.other,
                    fontWeight: selectedResource === r ? 600 : 400,
                  }}
                />
              ))}
              <Chip
                label="All"
                size="small"
                variant={selectedResource === null ? "filled" : "outlined"}
                color={selectedResource === null ? "primary" : "default"}
                onClick={() => setSelectedResource(null)}
                clickable
              />
            </>
          )}
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={fetchSummary} variant="outlined" size="small">
          Refresh
        </Button>
      </Box>

      {/* Run-wise AI Analysis */}
      {viewMode === "runs" && hasAnalyzedRuns && (
        <Box>
          {summary.analyzed_runs.map((runData) => (
            <AnalyzedRunCard key={runData.run_id} runData={runData} selectedResource={selectedResource} />
          ))}
        </Box>
      )}

      {/* Resource-wise view */}
      {viewMode === "resources" && hasResources && (
        <Box>
          {/* Resource overview cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {(selectedResource ? [selectedResource] : availableResources).map((resource) => {
              const data = summary.resources[resource];
              if (!data) return null;
              return (
                <Grid item xs={6} sm={4} md={3} key={`overview-${resource}`}>
                  <Paper
                    onClick={() => setSelectedResource(resource)}
                    sx={{
                      p: 2,
                      textAlign: "center",
                      borderTop: `4px solid ${resourceColors[resource] || resourceColors.other}`,
                      cursor: "pointer",
                      transition: "0.2s",
                      outline: selectedResource === resource ? `2px solid ${resourceColors[resource] || resourceColors.other}` : "none",
                      "&:hover": { boxShadow: 3 },
                    }}
                  >
                    <Box sx={{ color: resourceColors[resource] || resourceColors.other, mb: 1, display: "flex", justifyContent: "center" }}>
                      {resourceIcons[resource] || resourceIcons.other}
                    </Box>
                    <Typography variant="h5" fontWeight={700}>{data.total_actions}</Typography>
                    <Typography variant="body2" color="text.secondary">{resourceLabels[resource] || resource}</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={data.success_rate || 0}
                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                      color={data.success_rate >= 80 ? "success" : data.success_rate >= 50 ? "warning" : "error"}
                    />
                    <Typography variant="caption" color="text.secondary">{data.success_rate}% success</Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {(selectedResource ? [selectedResource] : availableResources).map((resource) => {
            const data = summary.resources[resource];
            if (!data) return null;
            return <ResourceSection key={resource} resource={resource} data={data} />;
          })}
        </Box>
      )}
    </Box>
  );
}

// --- Main Component ---

export default function WorkflowRunDashboard() {
  const { chatbotId, workflowUid } = useParams();
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tab, setTab] = useState(0);

  const fetchWorkflow = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/api/workflows/${workflowUid}`);
      setWorkflow(res.data.data || res.data);
    } catch {
      // Non-critical — header will fall back to generic title
    }
  }, [workflowUid]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/api/workflows/${workflowUid}/runs`, {
        params: { page, per_page: 20 },
      });
      setRuns(res.data.data || []);
      setTotalPages(res.data.pages || 1);
    } catch {
      setError("Failed to load run history.");
    } finally {
      setLoading(false);
    }
  }, [workflowUid, page]);

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  useEffect(() => {
    document.title = workflow?.name
      ? `${workflow.name} — Dashboard | Klonit`
      : "Workflow Dashboard | Klonit";
    fetchRuns();
  }, [fetchRuns, workflow?.name]);

  const modeIcon = workflow?.mode === "AI_AGENT" ? <SmartToyIcon fontSize="small" /> : <AccountTreeIcon fontSize="small" />;
  const modeLabel = workflow?.mode === "AI_AGENT" ? "AI Agent" : "Pipeline";
  const modeColor = workflow?.mode === "AI_AGENT" ? "secondary" : "primary";

  return (
    <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px", bgcolor: "background.paper", overflow: "hidden" }}>
      {/* Header with workflow title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small">
          Back
        </Button>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h5" fontWeight={700} color="textPrimary">
            {workflow?.name || "Workflow Dashboard"}
          </Typography>
          {workflow && (
            <>
              <Chip
                icon={modeIcon}
                label={modeLabel}
                size="small"
                color={modeColor}
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={workflow.status}
                size="small"
                color={workflow.status === "ACTIVE" ? "success" : workflow.status === "DRAFT" ? "warning" : "default"}
              />
            </>
          )}
        </Box>
        {workflow?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: "right" }}>
            {workflow.description}
          </Typography>
        )}
      </Box>
      <Divider sx={{ mb: 0 }} />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="History" />
        <Tab label="Console" />
        <Tab label="Analysis" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tab === 0 && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button startIcon={<RefreshIcon />} onClick={fetchRuns} variant="outlined" size="small">
              Refresh
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : runs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h6" color="text.secondary">
                No runs yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Execute the workflow to see run history here.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      <TableCell sx={{ width: 48 }} />
                      <TableCell sx={{ width: 80, fontWeight: 700 }}>Run ID</TableCell>
                      <TableCell sx={{ width: 110, fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ width: 100, fontWeight: 700 }}>Trigger</TableCell>
                      <TableCell sx={{ width: 80, fontWeight: 700 }}>Steps</TableCell>
                      <TableCell sx={{ width: 100, fontWeight: 700 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Started</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.map((run) => (
                      <RunRow key={run.id} run={run} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, v) => setPage(v)}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {tab === 1 && <ConsoleTab workflowUid={workflowUid} />}

      {tab === 2 && <ActivitySummaryTab workflowUid={workflowUid} />}
    </Paper>
  );
}
